import { computed } from 'vue';
import { OrderFlowRouteName } from './model.js';
import { getOrderFlowLastStepSaveCopy, getOrderFlowValidationEligibilities } from '../../../store/modules/orderFlow/orderFlowSteps/completionActions.js';
import { getIsDebugMode } from '../../../store/modules/variables/helpers.js';

/** Helpers used internally, but not exported to consuming repos */
const asChildNameByRootName = {
    [OrderFlowRouteName.ORDER_FLOW_BULK_UPLOAD]: OrderFlowRouteName.ORDER_FLOW_BULK_UPLOAD_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_PDF_UPLOAD]: OrderFlowRouteName.ORDER_FLOW_PDF_UPLOAD_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_MANUAL_INPUT]: OrderFlowRouteName.ORDER_FLOW_MANUAL_INPUT_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION]: OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_ORDER_MEDICAL_DETAILS]: OrderFlowRouteName.ORDER_FLOW_ORDER_MEDICAL_DETAILS_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_ORDER_PROVIDER]: OrderFlowRouteName.ORDER_FLOW_ORDER_PROVIDER_AS_CHILD,
    [OrderFlowRouteName.ORDER_FLOW_SCHEDULE]: OrderFlowRouteName.ORDER_FLOW_SCHEDULE_AS_CHILD,
};
/**
 * Returns back step in order. If one is not found (first step), returns
 * undefined and no back button will be shown.
 */
const getBackLocation = ({ store, route }) => { var _a; return (_a = store.getters['orderFlow/getBackStepRouteInOrder'](route)) !== null && _a !== void 0 ? _a : undefined; };
/**
 * Returns next step in order. If one is not found (last step), returns
 * undefined. The primary button will still show if there is a `confirmEvent`
 * available.
 */
const getNextLocation = ({ store, route }) => { var _a; return (_a = store.getters['orderFlow/getNextStepRouteInOrder'](route)) !== null && _a !== void 0 ? _a : undefined; };
/**
 * Returns next step in order. If one is not found (last step), returns
 * undefined and the "last step" button will show. (The primary
 * action on the order should be executed, which is "Send for scheduling.")
 */
const getNextLabel = (headingParams) => {
    const isLastStep = !getNextLocation(headingParams);
    // Note: not tested for reactivity, as no data changes are made on the last step.
    return isLastStep ? getOrderFlowLastStepSaveCopy() : 'Next';
};
const getNextDisabled = ({ store, route }) => computed(() => !store.getters['orderFlow/getIsCurrentStepValid'](route).value);
const getDebugModeString = (line) => `Debug: ${line}`;
const getDebugModeConfirmTooltipNotes = () => {
    if (!getIsDebugMode())
        return [];
    const eligiblities = Object.values(getOrderFlowValidationEligibilities());
    return eligiblities.map(getDebugModeString);
};
const getConfirmTooltip = ({ store, route }) => {
    var _a;
    const failures = (_a = store.getters['orderFlow/getCurrentStepValidationFailures'](route)) !== null && _a !== void 0 ? _a : [];
    const debugModeNotes = getDebugModeConfirmTooltipNotes();
    const lines = [...failures, ...debugModeNotes];
    if (lines.length)
        return lines.join('\n');
};
const getConfirmLoading = ({ store }) => store.state.orderFlow.localState.nextButtonLoading;
const getConfirmEvent = ({ store, route }) => {
    const hasCompletionAction = !!store.getters['orderFlow/getCurrentStepCompletionAction'](route);
    if (!hasCompletionAction)
        return;
    return () => store.dispatch('orderFlow/performCompletionAction');
};
const getSecondaryComponentAndProps = ({ store, route }) => store.getters['orderFlow/getCurrentStepSecondaryComponent'](route);
const getTitle = ({ store, route }, title) => { var _a; return (_a = store.getters['orderFlow/getCurrentStepTitle'](route)) !== null && _a !== void 0 ? _a : title; };
const getOrderFlowMaybeAsChildName = (rootName, asChild) => asChild ? asChildNameByRootName[rootName] : rootName;
const orderFlowHeadingWithNav = (title, disableBack = false) => (headingParams) => computed(() => {
    var _a, _b;
    return ({
        backLocation: !disableBack ? getBackLocation(headingParams) : undefined,
        confirmDisabled: getNextDisabled(headingParams),
        confirmEvent: getConfirmEvent(headingParams),
        confirmLoading: getConfirmLoading(headingParams),
        mobileFooter: true,
        confirmText: getNextLabel(headingParams),
        // Currently takes precedence over confirmEvent
        confirmTo: getNextLocation(headingParams),
        confirmTooltip: getConfirmTooltip(headingParams),
        leftAlignTitle: true,
        secondaryComponent: (_a = getSecondaryComponentAndProps(headingParams)) === null || _a === void 0 ? void 0 : _a.component,
        secondaryComponentProps: (_b = getSecondaryComponentAndProps(headingParams)) === null || _b === void 0 ? void 0 : _b.props,
        title: getTitle(headingParams, title),
    });
});

export { asChildNameByRootName, getOrderFlowMaybeAsChildName, orderFlowHeadingWithNav };
